import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { format } from 'date-fns';

const formatError = (err, fallback) => {
  return [err?.message, err?.details, err?.hint].filter(Boolean).join(' ') || fallback;
};

const readFunctionError = async (err) => {
  if (!err?.context) return formatError(err, 'Unable to generate document.');

  try {
    const contentType = err.context.headers?.get?.('content-type') || '';
    if (contentType.includes('application/json')) {
      const payload = await err.context.json();
      return payload?.error || payload?.message || formatError(err, 'Unable to generate document.');
    }

    const text = await err.context.text();
    return text || formatError(err, 'Unable to generate document.');
  } catch (parseErr) {
    console.warn('Unable to parse Edge Function error response:', parseErr);
    return formatError(err, 'Unable to generate document.');
  }
};

const DocumentGenerationPanel = ({ matter, onDocumentGenerated, showHistory = true }) => {
  const { profile } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [generatedDocuments, setGeneratedDocuments] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);

  const hasSubmittedIntake = matter?.status !== 'matter_created' && matter?.status !== 'intake_in_progress';

  const fetchData = async () => {
    if (!matter?.id || !profile?.law_firm_id) return;

    try {
      setLoading(true);
      setError(null);

      const [templatesRes, generatedRes] = await Promise.all([
        supabase
          .from('document_templates')
          .select('*')
          .or(`law_firm_id.is.null,law_firm_id.eq.${profile.law_firm_id}`)
          .eq('status', 'active')
          .order('name', { ascending: true }),
        supabase
          .from('generated_documents')
          .select('*, template:document_templates(name)')
          .eq('matter_id', matter.id)
          .order('created_at', { ascending: false })
      ]);

      if (templatesRes.error) throw templatesRes.error;
      if (generatedRes.error) throw generatedRes.error;

      setTemplates(templatesRes.data || []);
      setGeneratedDocuments(generatedRes.data || []);
      setSelectedTemplateId((prev) => prev || templatesRes.data?.[0]?.id || '');
    } catch (err) {
      console.error('Document generation panel error:', err);
      setError(formatError(err, 'Unable to load document templates.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [matter?.id, profile?.law_firm_id]);

  const handleGenerate = async () => {
    if (!matter?.id || !selectedTemplateId || generating) return;

    try {
      setGenerating(true);
      setError(null);
      setNotice(null);

      const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
      if (sessionErr) throw sessionErr;

      const accessToken = sessionData?.session?.access_token;
      if (!accessToken) {
        throw new Error('Your login session was not available. Please sign out and sign back in, then try again.');
      }

      const { data, error: invokeErr } = await supabase.functions.invoke('generate-document', {
        headers: {
          Authorization: `Bearer ${accessToken}`
        },
        body: {
          matter_id: matter.id,
          document_template_id: selectedTemplateId
        }
      });

      if (invokeErr) {
        throw new Error(await readFunctionError(invokeErr));
      }
      if (data?.error) throw new Error(data.error);

      setNotice('Document generated and added to the matter documents.');
      await fetchData();
      onDocumentGenerated?.(data);
    } catch (err) {
      console.error('Generate document error:', err);
      setError(formatError(err, 'Unable to generate document.'));
      await fetchData();
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="bg-white shadow-sm rounded-xl border border-slate-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
        <h3 className="font-bold text-slate-800 flex items-center text-sm uppercase tracking-tight">
          <SafeIcon icon={FiIcons.FiFilePlus} className="mr-2 h-4 w-4 text-slate-400" />
          Document Templates
        </h3>
        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
          DocxTemplater
        </span>
      </div>

      <div className="p-6 space-y-4">
        {notice && (
          <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded-md text-sm text-green-700">
            {notice}
          </div>
        )}
        {error && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md text-sm text-red-700">
            {error}
          </div>
        )}

        {loading ? (
          <div className="py-8 text-center text-slate-400">
            <SafeIcon icon={FiIcons.FiLoader} className="animate-spin h-6 w-6 mx-auto mb-2" />
            <p className="text-sm">Loading templates...</p>
          </div>
        ) : templates.length > 0 ? (
          <div className="flex flex-col md:flex-row gap-3">
            <select
              value={selectedTemplateId}
              onChange={(e) => setSelectedTemplateId(e.target.value)}
              className="flex-1 block w-full sm:text-sm border-slate-300 rounded-md py-2 px-3 border focus:ring-slate-500 focus:border-slate-500 outline-none bg-white"
            >
              {templates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name} v{template.version}
                </option>
              ))}
            </select>
            <button
              onClick={handleGenerate}
              disabled={!selectedTemplateId || generating || !hasSubmittedIntake}
              className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {generating ? (
                <>
                  <SafeIcon icon={FiIcons.FiLoader} className="animate-spin mr-2 h-4 w-4" />
                  Generating...
                </>
              ) : (
                <>
                  <SafeIcon icon={FiIcons.FiZap} className="mr-2 h-4 w-4" />
                  Generate
                </>
              )}
            </button>
          </div>
        ) : (
          <div className="py-8 text-center text-slate-500">
            <SafeIcon icon={FiIcons.FiFileText} className="h-10 w-10 text-slate-200 mx-auto mb-3" />
            <p className="text-sm">No active document templates are available yet.</p>
            <Link to="/law-firm/templates" className="inline-flex mt-3 text-sm font-semibold text-blue-700 hover:text-blue-800">Manage templates</Link>
          </div>
        )}

        {!hasSubmittedIntake && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-3 py-2">
            Intake should be submitted before generating legal documents.
          </p>
        )}
      </div>

      {showHistory && generatedDocuments.length > 0 && (
        <div className="border-t border-slate-100">
          <div className="px-6 py-3 bg-slate-50/50">
            <h4 className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Generation History</h4>
          </div>
          <div className="divide-y divide-slate-100">
            {generatedDocuments.map((document) => (
              <div key={document.id} className="p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-slate-900">
                    {document.file_name || document.template?.name || 'Generated document'}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    {document.status} - {format(new Date(document.created_at), 'MMM d, yyyy h:mm a')}
                  </p>
                  {document.error_message && (
                    <p className="text-xs text-red-600 mt-1">{document.error_message}</p>
                  )}
                </div>
                <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest rounded-full border self-start ${
                  document.status === 'completed'
                    ? 'bg-green-50 text-green-700 border-green-100'
                    : document.status === 'failed'
                      ? 'bg-red-50 text-red-700 border-red-100'
                      : 'bg-blue-50 text-blue-700 border-blue-100'
                }`}>
                  {document.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentGenerationPanel;
